/**
 * Chemin du temps — logique pure (§6, étape 8).
 *
 * Un idle ne compte pas les images, il compte les millisecondes. Encore faut-il savoir
 * **de quelles millisecondes on parle** : celles où le joueur regardait sa ville, ou
 * celles où l'onglet dormait dans un autre bureau. Trois défauts de l'audit tenaient à
 * cette confusion, et ils se corrigent au même endroit.
 *
 * **P2.1 — le temps de tick n'était pas borné.** `elapsed = now - lastTickAt` créditait
 * l'intégralité de l'écart à plein tarif : un onglet laissé ouvert une nuit rendait huit
 * heures de production pleine, là où la conception accorde huit heures **à 25 %**. Un
 * joueur qui ferme le jeu était donc puni par rapport à un joueur qui le laisse ouvert
 * en arrière-plan — exactement l'incitation perverse que `balance.ts` dit avoir
 * corrigée, réintroduite en pire. Pendant ce temps, `offlineEarnings`, écrit et testé,
 * n'avait **aucun appelant**.
 *
 * **P2.3 — la bascule de période n'était pas monotone.** Reculer l'horloge système
 * rouvrait les quêtes déjà encaissées : osciller entre J et J+1 donnait des gemmes à
 * l'infini. La monotonie est traitée dans `quests.ts`, au plus près de l'état concerné.
 *
 * Ce module ne connaît ni horloge ni stockage : on lui donne un écart, il dit comment le
 * répartir. C'est ce qui le rend testable sans navigateur et rejouable à l'identique.
 */

import { ECONOMY } from '@/config/balance';

/**
 * Écart maximal crédité à plein tarif, en millisecondes.
 *
 * Deux fois l'intervalle de tick : de quoi absorber une frame en retard, un garbage
 * collector ou un `setInterval` étranglé par le navigateur, sans jamais laisser passer
 * une vraie absence. Au-delà, ce n'est plus un hoquet, c'est le joueur qui est parti.
 */
export const MAX_TICK_MS = Math.round((1000 / ECONOMY.tickHz) * 2);

export interface ElapsedSplit {
  /** Temps à créditer plein tarif, comme si le joueur regardait. */
  readonly liveMs: number;
  /**
   * Temps à traiter comme une absence : plafonné et amorti par `offlineEarnings`.
   * Vaut 0 pour un tick ordinaire — le cas courant ne coûte donc rien.
   */
  readonly awayMs: number;
}

/**
 * Répartit un écart de temps entre présence et absence.
 *
 * Un écart NÉGATIF rend zéro partout : l'horloge système a reculé (changement d'heure,
 * synchronisation NTP, ou main du joueur sur les réglages). Créditer un temps négatif
 * retirerait des pièces ; le traiter comme une absence en offrirait. On ne fait rien,
 * ce qui est la seule réponse honnête à une horloge qui ment.
 */
export function splitElapsed(elapsedMs: number): ElapsedSplit {
  if (!Number.isFinite(elapsedMs) || elapsedMs <= 0) {
    return { liveMs: 0, awayMs: 0 };
  }

  if (elapsedMs <= MAX_TICK_MS) {
    return { liveMs: elapsedMs, awayMs: 0 };
  }

  return { liveMs: MAX_TICK_MS, awayMs: elapsedMs - MAX_TICK_MS };
}
